import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { createRef, FocusEventHandler, FunctionComponent, useMemo } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

import {
  brandAtom,
  modelAtom,
  setversionAtom,
} from '../../../../../hooks/use-picker-selection';
import { fetchModels } from '../../../../../services/vehicles';
import Select, {
  CustomClearIndicator,
  CustomDownChevron,
  CustomMenuParts,
  CustomMenuWithoutMobile,
  CustomOptionModelsVersions,
  CustomSingleValueModelsVersions,
} from '../../../../ui/inputs/custom-selector';

export const modelRef = createRef();

const loadModels = async ({ queryKey: [_, locale, slug] }) => {
  const data = await fetchModels({ locale, slug });

  return Object.entries(data.children).map(([key, value]) => ({
    label: key,
    options: value.map((item) => ({
      value: item.slug,
      label: item.name,
      start_date: item.start_date,
      end_date: item.end_date || new Date().getFullYear(),
      hasVersions: item.have_versions,
    })),
  }));
};

const ModelSelect: FunctionComponent<{
  onFocus?: FocusEventHandler;
  register?: UseFormRegisterReturn;
  error?: Array<string>;
  setValue?: any;
  models?: any;
  isModelsLoading?: any;
}> = ({ onFocus, register, error, setValue, models, isModelsLoading }) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const [brand] = useAtom(brandAtom);
  const [model, setModel] = useAtom(modelAtom);
  const [, setVersion] = useAtom(setversionAtom);

  const modelOptions = useMemo(() => {
    if (models && models.children) {
      const final = Object.entries(models.children)
      return final.map(([groupName, items]) => {
        return {
          label: groupName,
          options: items.map(item => ({
            ...item,
            value: item.slug,
            label: item.name
          }))
        };
      });
    }

    return models;

  }, [models])

  return (
    <Select
      {...register}
      ref={modelRef}
      id="model-input"
      aria-label={t('vehicle-picker:chooseModel')}
      openMenuOnFocus
      value={model}
      isDisabled={!brand}
      isLoading={!!brand && isModelsLoading}
      loadingMessage={() => t('common:loading')}
      options={modelOptions}
      components={{
        Option: CustomOptionModelsVersions,
        SingleValue: CustomSingleValueModelsVersions,
        DropdownIndicator: CustomDownChevron,
        MenuList: CustomMenuParts,
        ClearIndicator: CustomClearIndicator,
      }}
      onFocus={onFocus}
      onChange={(item) => {
        setModel(item);
        setVersion(null);
        setValue('slug', { brand: brand, model: item });
      }}
      placeholder={t('vehicle-picker:chooseModel')}
      error={error}
      isClearable
    />
  );
};

export default ModelSelect;
